import { useEffect, useState } from 'react';
import Animated, {
  Easing,
  useAnimatedProps,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';

const AnimatedPath = Animated.createAnimatedComponent(Path);

/**
 * Checkmark on a 24x24 grid, written to start at the short leg so the stroke
 * draws the way the mark is written by hand. Lucide's own `Check` runs the other
 * way, from the long tail down, which draws backwards.
 */
const CHECK_PATH = 'M4 12L9 17L20 6';

/**
 * Length of {@link CHECK_PATH}, the two legs summed: √(5²+5²) + √(11²+11²). The
 * stroke is hidden by offsetting a dash this long and animated to zero, so the
 * value has to match the path or the mark starts part-drawn or never finishes.
 */
const CHECK_PATH_LENGTH = 22.63;

type AnimatedCheckProps = {
  /** Rendered width and height in px. */
  size?: number;
  /** Stroke color; any valid SVG paint value. */
  color?: string;
  /** Stroke width on the 24-unit grid, matching Lucide's default weight. */
  strokeWidth?: number;
  /** Delay before the stroke starts drawing (ms). */
  delay?: number;
  /** How long the stroke takes to draw (ms). */
  duration?: number;
};

/**
 * A checkmark that draws itself, for confirming an action.
 *
 * Lucide's icons are static SVG, so one from the set can only be moved or scaled
 * as a whole. This owns the path and animates its `strokeDashoffset`, which is
 * what makes the mark look written rather than merely present.
 *
 * The viewBox is inset 3 units because a 2.5-wide round-capped stroke reaches
 * past the 24-unit grid and an `svg` clips to its viewBox: under a caller's
 * scale animation the top-right tip would be the first thing cropped.
 *
 * Honors reduced motion by rendering fully drawn.
 */
export function AnimatedCheck({
  size = 32,
  color = 'currentColor',
  strokeWidth = 2.5,
  delay = 0,
  duration = 420,
}: AnimatedCheckProps) {
  const drawn = useSharedValue(0);

  const reducedMotion = useReducedMotion();

  /**
   * True once the draw has had its full time, after which a plain `Path` at zero
   * offset replaces the animated one.
   *
   * The animation is a progressive enhancement, not the thing that makes the
   * mark appear. Animating an SVG attribute through `useAnimatedProps` is the
   * least reliable corner of reanimated on web, and a stall there leaves the
   * stroke frozen part-drawn — which, because the long leg runs up to the
   * top-right, reads as a check with its top missing. Handing over to a static
   * path guarantees the resting state whatever the animation did.
   */
  const [settled, setSettled] = useState(reducedMotion);

  useEffect(() => {
    if (reducedMotion) {
      drawn.value = 1;

      setSettled(true);

      return;
    }

    drawn.value = withDelay(delay, withTiming(1, { duration, easing: Easing.out(Easing.cubic) }));

    const timer = setTimeout(() => setSettled(true), delay + duration);

    return () => clearTimeout(timer);
  }, [drawn, reducedMotion, delay, duration]);

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: CHECK_PATH_LENGTH * (1 - drawn.value),
  }));

  const stroke = {
    d: CHECK_PATH,
    stroke: color,
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;

  return (
    <Svg width={size} height={size} viewBox="-3 -3 30 30" fill="none">
      {settled ? (
        <Path {...stroke} />
      ) : (
        <AnimatedPath
          {...stroke}
          strokeDasharray={CHECK_PATH_LENGTH}
          strokeDashoffset={CHECK_PATH_LENGTH}
          animatedProps={animatedProps}
        />
      )}
    </Svg>
  );
}
